import React from 'react';
import {
  Typography,
  Box,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { Commission, PROVIDERS, useProviders } from '../../hooks/admin/useAdminCommissions';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  dangerBtnSx,
  ledgerChipSx,
  neutralChipSx,
  fieldLabelSx,
} from '../../styles/ledger';

interface CommissionViewProps {
  commission: Commission | undefined;
  isLoading: boolean;
  error: Error | null;
  onEdit: () => void;
  onDelete: () => void;
  onBack: () => void;
  isDeleting: boolean;
}

const CommissionView: React.FC<CommissionViewProps> = ({
  commission,
  isLoading,
  error,
  onEdit,
  onDelete,
  onBack,
  isDeleting,
}) => {
  const { data: providerOptions = PROVIDERS } = useProviders();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12, bgcolor: INK.bg, minHeight: '60vh' }}>
        <CircularProgress size={32} sx={{ color: INK.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, bgcolor: INK.bg, minHeight: '60vh' }}>
        <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans }}>
          Error loading commission: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!commission) {
    return (
      <Box sx={{ p: 4, bgcolor: INK.bg, minHeight: '60vh' }}>
        <Alert severity="info" sx={{ borderRadius: 0, fontFamily: FONTS.sans }}>
          Commission not found.
        </Alert>
      </Box>
    );
  }

  const getZoneDisplay = (zone: any, zoneType: string | null | undefined) => {
    if (!zone || !zoneType) return 'Any zone';
    return zone.name || zone.code || '—';
  };

  const zonePrefix = (type: string | null | undefined) => {
    switch (type) {
      case 'country': return 'COUNTRY';
      case 'state': return 'STATE';
      case 'city': return 'CITY';
      default: return 'ZONE';
    }
  };

  const isTiered = !!commission.commission_tiers && commission.commission_tiers.length > 0;
  const hasFlat = commission.value_amount !== undefined && commission.value_amount !== null;

  const createdDate = new Date(commission.created_at);
  const updatedDate = new Date(commission.updated_at);
  const isUpdatedSameDay = createdDate.toDateString() === updatedDate.toDateString();

  return (
    <Box
      sx={{
        fontFamily: FONTS.sans,
        bgcolor: INK.bg,
        color: INK.text,
        mx: { xs: -2, md: -3 },
        my: { xs: -2, md: -3 },
        px: { xs: 3, md: 6 },
        py: { xs: 4, md: 5 },
        minHeight: 'calc(100vh - 64px)',
        backgroundImage:
          'radial-gradient(ellipse at top left, rgba(31,78,61,0.025), transparent 50%), radial-gradient(ellipse at bottom right, rgba(184,101,27,0.02), transparent 50%)',
      }}
    >
      {/* ─── BACK + ACTION BAR ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{
            ...ghostBtnSx,
            border: 'none',
            color: INK.muted,
            px: 1,
            '&:hover': { bgcolor: 'transparent', color: INK.accent, borderColor: 'transparent' },
          }}
        >
          Back to ledger
        </Button>
        <Stack direction="row" spacing={1.5}>
          <Button onClick={onEdit} startIcon={<EditIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
            Edit
          </Button>
          <Button
            onClick={onDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
            sx={dangerBtnSx}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Stack>
      </Stack>

      {/* ─── HEADER ─── */}
      <Box sx={{ mb: 5, animation: 'fadeUp 0.5s ease-out', '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1 } } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 11,
              letterSpacing: '0.18em',
              color: INK.accent,
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Admin · Ledger · Entry
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'baseline' }} spacing={2}>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONTS.serif,
              fontSize: { xs: 52, md: 72 },
              lineHeight: 0.92,
              letterSpacing: '-0.02em',
              color: INK.text,
              fontWeight: 400,
            }}
          >
            Commission
            <Box component="span" sx={{ fontFamily: FONTS.mono, fontSize: { xs: 32, md: 44 }, fontStyle: 'normal', color: INK.accent, ml: 2 }}>
              № {String(commission.id).padStart(3, '0')}
            </Box>
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.6,
              border: `1px solid ${commission.is_active ? INK.accent : INK.inactive}`,
              bgcolor: commission.is_active ? INK.accentSoft : INK.inactiveSoft,
              borderRadius: 0.5,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: commission.is_active ? INK.active : INK.inactive,
              }}
            />
            <Typography
              sx={{
                fontFamily: FONTS.mono,
                fontSize: 10.5,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: commission.is_active ? INK.active : INK.inactive,
                fontWeight: 600,
              }}
            >
              {commission.is_active ? 'Active' : 'Paused'}
            </Typography>
          </Box>
          {commission.users && commission.users.length > 0 ? (
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted, letterSpacing: '0.04em' }}>
              User-scoped · {commission.users.length} {commission.users.length === 1 ? 'user' : 'users'}
            </Typography>
          ) : (
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted, letterSpacing: '0.04em' }}>
              General · applies to all
            </Typography>
          )}
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.subtle, letterSpacing: '0.04em' }}>
            Priority {commission.priority}
          </Typography>
        </Stack>
      </Box>

      {/* ─── KPI STRIP ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          mb: 5,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          animation: 'fadeUp 0.6s ease-out',
        }}
      >
        <KPI label="Type" value={isTiered ? 'Tiered' : hasFlat ? 'Flat' : '—'} sub={isTiered ? `${commission.commission_tiers!.length} bands` : 'single rate'} />
        <KPI
          label="Headline rate"
          value={renderHeadlineRate(commission)}
          sub={isTiered ? 'min – max' : commission.value_type === 'percentage' ? 'percentage' : commission.value_type === 'fixed' ? 'ILS fixed' : '—'}
          accent
        />
        <KPI label="Created" value={createdDate.toLocaleDateString('en-CA')} sub={createdDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
        <KPI label="Updated" value={updatedDate.toLocaleDateString('en-CA')} sub={isUpdatedSameDay ? 'same as created' : 'last revised'} isLast />
      </Box>

      {/* ─── ASSIGNMENT + CHANNEL ─── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 5 }}>
        {/* Assignment */}
        <Box
          sx={{
            flex: 1,
            border: `1px solid ${INK.hairline}`,
            bgcolor: INK.surface,
            borderRadius: 0.5,
            p: 3,
          }}
        >
          <SectionHeader number="01" eyebrow="Assignment" title="Who it applies to" />
          {commission.users && commission.users.length > 0 ? (
            <Stack spacing={1.5}>
              {commission.users.map((user) => (
                <Box key={user.id} sx={{ pb: 1.5, borderBottom: `1px solid ${INK.hairlineSoft}`, '&:last-of-type': { borderBottom: 'none', pb: 0 } }}>
                  <Typography sx={{ fontSize: 15, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.subtle }}>
                    {user.email}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box>
              <Typography
                sx={{
                  display: 'inline-block',
                  fontFamily: FONTS.mono,
                  fontSize: 10.5,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: INK.accent,
                  border: `1px solid ${INK.accent}`,
                  bgcolor: INK.accentSoft,
                  px: 1,
                  py: 0.4,
                  fontWeight: 500,
                  mb: 1.5,
                }}
              >
                General
              </Typography>
              <Typography sx={{ fontSize: 13, color: INK.muted, lineHeight: 1.55 }}>
                Applies to every user that doesn't have a more specific override.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Channel */}
        <Box
          sx={{
            flex: 1,
            border: `1px solid ${INK.hairline}`,
            bgcolor: INK.surface,
            borderRadius: 0.5,
            p: 3,
          }}
        >
          <SectionHeader number="02" eyebrow="Channel" title="Provider & services" />
          <Box sx={{ mb: 2 }}>
            <Typography sx={fieldLabelSx}>Providers</Typography>
            {commission.providers && commission.providers.length > 0 ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {commission.providers.map((p) => (
                  <Box
                    key={p}
                    sx={{
                      fontFamily: FONTS.mono,
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: INK.text,
                      border: `1px solid ${INK.hairline}`,
                      bgcolor: INK.surfaceTint,
                      px: 1,
                      py: 0.35,
                      fontWeight: 600,
                    }}
                  >
                    {providerOptions.find((x) => x.value === p)?.label || p}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.subtle }}>All providers</Typography>
            )}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Service types</Typography>
            {commission.service_types && commission.service_types.length > 0 ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {commission.service_types.map((st) => (
                  <Chip key={st} label={st.replace(/_/g, ' ').toLowerCase()} size="small" sx={{ ...neutralChipSx, textTransform: 'lowercase' }} />
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.subtle }}>All service types</Typography>
            )}
          </Box>
        </Box>
      </Stack>

      {/* ─── CORRIDOR ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          p: 3,
          mb: 5,
        }}
      >
        <SectionHeader number="03" eyebrow="Corridor" title="Origin & destination" />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <ZoneCard
            label="Origin"
            zones={commission.origin_zone_details}
            zonePrefix={zonePrefix}
            getZoneDisplay={getZoneDisplay}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
              color: INK.faint,
              transform: { xs: 'rotate(90deg)', md: 'none' },
              py: { xs: 1, md: 0 },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 22 }} />
          </Box>
          <ZoneCard
            label="Destination"
            zones={commission.destination_zone_details}
            zonePrefix={zonePrefix}
            getZoneDisplay={getZoneDisplay}
          />
        </Stack>
      </Box>

      {/* ─── RATES ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          p: 3,
          mb: 5,
        }}
      >
        <SectionHeader number="04" eyebrow="Rates" title={isTiered ? 'Tiered commission ladder' : 'Commission value'} />
        {isTiered ? (
          <Box sx={{ border: `1px solid ${INK.hairline}`, borderRadius: 0.5, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        fontFamily: FONTS.mono,
                        fontSize: 10,
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: INK.subtle,
                        fontWeight: 500,
                        borderBottom: `1px solid ${INK.hairline}`,
                        bgcolor: INK.surfaceTint,
                        py: 1.25,
                      },
                    }}
                  >
                    <TableCell width={48}>№</TableCell>
                    <TableCell>Min weight</TableCell>
                    <TableCell>Max weight</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commission.commission_tiers!.map((tier, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '& .MuiTableCell-root': { borderBottom: `1px solid ${INK.hairlineSoft}`, py: 1.5 },
                        '&:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                      }}
                    >
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted }}>
                        {String(index + 1).padStart(2, '0')}
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text }}>
                        {tier.weight_min} kg
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text }}>
                        {tier.weight_max === null ? (
                          <Box component="span" sx={{ color: INK.subtle }}>∞ (and above)</Box>
                        ) : (
                          `${tier.weight_max} kg`
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 14, color: INK.text, fontWeight: 600, letterSpacing: '-0.01em' }}>
                        {tier.value_amount}
                        {tier.value_type === 'percentage' ? '%' : ''}
                      </TableCell>
                      <TableCell>
                        <Chip label={tier.value_type === 'fixed' ? 'ILS' : '%'} size="small" sx={neutralChipSx} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : hasFlat ? (
          <Stack direction="row" alignItems="baseline" spacing={2}>
            <Typography
              sx={{
                fontFamily: FONTS.serif,
                fontSize: { xs: 72, md: 96 },
                lineHeight: 0.9,
                letterSpacing: '-0.03em',
                color: INK.accent,
                fontWeight: 400,
              }}
            >
              {commission.value_amount}
              <Box component="span" sx={{ fontSize: { xs: 36, md: 48 }, ml: 0.5 }}>
                {commission.value_type === 'percentage' ? '%' : ''}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTS.mono,
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: INK.muted,
              }}
            >
              {commission.value_type === 'fixed' ? 'ILS · fixed amount' : 'percentage'}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 32, color: INK.subtle, letterSpacing: '-0.01em' }}>
            No rate configured.
          </Typography>
        )}
      </Box>

      {/* ─── CONDITIONS ─── */}
      {(commission.conditions?.package_type ||
        commission.conditions?.weight?.min !== undefined ||
        commission.conditions?.weight?.max !== undefined) && (
        <Box
          sx={{
            border: `1px solid ${INK.hairline}`,
            bgcolor: INK.surface,
            borderRadius: 0.5,
            p: 3,
            mb: 5,
          }}
        >
          <SectionHeader number="05" eyebrow="Conditions" title="Additional constraints" />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            {commission.conditions?.package_type && (
              <Box>
                <Typography sx={fieldLabelSx}>Package type</Typography>
                <Chip label={commission.conditions.package_type} size="small" sx={ledgerChipSx} />
              </Box>
            )}
            {(commission.conditions?.weight?.min !== undefined ||
              commission.conditions?.weight?.max !== undefined) && (
              <Box>
                <Typography sx={fieldLabelSx}>Weight range (kg)</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontFamily: FONTS.mono, fontSize: 14, color: INK.text, fontWeight: 500 }}>
                    {commission.conditions.weight?.min ?? '0'}
                  </Typography>
                  <Box component="span" sx={{ color: INK.faint, fontFamily: FONTS.mono, fontSize: 14 }}>—</Box>
                  <Typography sx={{ fontFamily: FONTS.mono, fontSize: 14, color: INK.text, fontWeight: 500 }}>
                    {commission.conditions.weight?.max ?? '∞'}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Footer mark */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, opacity: 0.6 }}>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em', color: INK.subtle, textTransform: 'uppercase' }}>
          Almaship · Commission · № {String(commission.id).padStart(3, '0')}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle }}>
          {createdDate.toISOString().split('T')[0]}
        </Typography>
      </Stack>
    </Box>
  );
};

/* ─── KPI cell ─── */
const KPI: React.FC<{ label: string; value: React.ReactNode; sub: string; accent?: boolean; isLast?: boolean }> = ({
  label,
  value,
  sub,
  accent,
  isLast,
}) => (
  <Box
    sx={{
      px: { xs: 2.5, md: 3.5 },
      py: { xs: 2.5, md: 3 },
      borderRight: { md: isLast ? 'none' : `1px solid ${INK.hairline}` },
      borderBottom: { xs: isLast ? 'none' : `1px solid ${INK.hairline}`, md: 'none' },
    }}
  >
    <Typography
      sx={{
        fontFamily: FONTS.mono,
        fontSize: 10,
        letterSpacing: '0.16em',
        color: INK.subtle,
        textTransform: 'uppercase',
        mb: 1.5,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONTS.serif,
        fontSize: { xs: 32, md: 42 },
        lineHeight: 0.95,
        color: accent ? INK.accent : INK.text,
        letterSpacing: '-0.02em',
        mb: 0.5,
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ fontFamily: FONTS.sans, fontSize: 11.5, color: INK.subtle }}>{sub}</Typography>
  </Box>
);

/* ─── Section header ─── */
const SectionHeader: React.FC<{ number: string; eyebrow: string; title: string }> = ({ number, eyebrow, title }) => (
  <Box sx={{ mb: 2.5 }}>
    <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.accent, fontWeight: 600, letterSpacing: '0.04em' }}>
        § {number}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONTS.mono,
          fontSize: 10,
          letterSpacing: '0.18em',
          color: INK.accent,
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {eyebrow}
      </Typography>
    </Stack>
    <Typography
      sx={{
        fontFamily: FONTS.serif,
        fontSize: 22,
        letterSpacing: '-0.01em',
        color: INK.text,
        fontWeight: 400,
        lineHeight: 1.15,
      }}
    >
      {title}
    </Typography>
  </Box>
);

/* ─── Zone card (origin / destination) ─── */
const ZoneCard: React.FC<{
  label: string;
  zones?: Array<{ type: string; id: number; data: any }>;
  zonePrefix: (t: string | null | undefined) => string;
  getZoneDisplay: (z: any, t: string | null | undefined) => string;
}> = ({ label, zones, zonePrefix, getZoneDisplay }) => (
  <Box
    sx={{
      flex: 1,
      border: `1px solid ${INK.hairline}`,
      borderRadius: 0.5,
      p: 2.5,
      bgcolor: INK.surfaceTint,
    }}
  >
    <Typography
      sx={{
        fontFamily: FONTS.mono,
        fontSize: 10,
        letterSpacing: '0.18em',
        color: INK.accent,
        fontWeight: 600,
        textTransform: 'uppercase',
        mb: 1.5,
      }}
    >
      {label}
    </Typography>
    {zones && zones.length > 0 ? (
      <Stack spacing={1.5}>
        {zones.map((z, i) => (
          <Box key={i} sx={{ pb: 1, borderBottom: `1px solid ${INK.hairlineSoft}`, '&:last-of-type': { borderBottom: 'none', pb: 0 } }}>
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.faint, letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.25 }}>
              {zonePrefix(z.type)}
            </Typography>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.text, letterSpacing: '-0.01em' }}>
              {getZoneDisplay(z.data, z.type)}
            </Typography>
          </Box>
        ))}
      </Stack>
    ) : (
      <Typography sx={{ fontFamily: FONTS.serif, fontSize: 24, color: INK.subtle, letterSpacing: '-0.01em' }}>
        Any {label.toLowerCase()}
      </Typography>
    )}
  </Box>
);

/* ─── Headline rate formatter ─── */
function renderHeadlineRate(c: Commission): string {
  if (c.commission_tiers && c.commission_tiers.length > 0) {
    const values = c.commission_tiers.map((t) => Number(t.value_amount) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const allPercent = c.commission_tiers.every((t) => t.value_type === 'percentage');
    const suffix = allPercent ? '%' : '';
    return min === max ? `${min}${suffix}` : `${min}–${max}${suffix}`;
  }
  if (c.value_amount !== undefined && c.value_amount !== null) {
    return `${c.value_amount}${c.value_type === 'percentage' ? '%' : ''}`;
  }
  return '—';
}

export default CommissionView;
